import { useEffect, useState } from "react";
import { BookOpen, CalendarDays, Car, Clock, HeartPulse, Folder, FolderPlus, Pencil, Receipt, Scale, Trash2, Truck, Check, Info } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useVault } from "@/context/VaultContext";
import { db } from "@/lib/db";
import { fmtBytes, uuid } from "@/lib/util";

const ICONS = { scale: Scale, truck: Truck, receipt: Receipt, car: Car, book: BookOpen, clock: Clock, heart: HeartPulse };

function FolderRow({ f, count, active, onSelect, onDone }) {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(f.name);
  const Icon = ICONS[f.icon] || Folder;
  const save = async () => { if (name.trim()) await db.put("folders", { ...f, name: name.trim() }); setEdit(false); onDone(); };
  const remove = async () => {
    if (!window.confirm(`Delete folder "${f.name}"? Its notes move to Daily Journal.`)) return;
    for (const n of await db.byIndex("notes", "folderId", f.id)) await db.put("notes", { ...n, folderId: "daily-journal" });
    await db.del("folders", f.id);
    onDone(true);
  };
  if (edit) return (
    <div className="flex gap-1 px-2 py-1">
      <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8" data-testid={`folder-rename-input-${f.id}`} autoFocus onKeyDown={(e) => e.key === "Enter" && save()} />
      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={save} data-testid={`folder-rename-save-${f.id}`}><Check className="h-4 w-4" /></Button>
    </div>
  );
  return (
    <div className={`group flex items-center rounded-md transition-colors ${active ? "bg-accent text-accent-foreground" : "hover:bg-muted"}`}>
      <button type="button" onClick={onSelect} data-testid={`folder-item-${f.id}`} className="flex-1 flex items-center gap-3 px-3 py-2.5 text-left text-sm min-w-0">
        <Icon className={`h-4 w-4 shrink-0 ${active ? "text-primary" : "text-muted-foreground"}`} />
        <span className="truncate">{f.name}</span>
        <span className="ml-auto font-mono text-xs text-muted-foreground" data-testid={`folder-count-${f.id}`}>{count}</span>
      </button>
      {!f.builtin && (
        <div className="flex opacity-100 lg:opacity-0 group-hover:opacity-100 transition-opacity pr-1">
          <button type="button" onClick={() => setEdit(true)} className="p-1.5 text-muted-foreground hover:text-foreground" data-testid={`folder-rename-${f.id}`}><Pencil className="h-3.5 w-3.5" /></button>
          <button type="button" onClick={remove} className="p-1.5 text-muted-foreground hover:text-destructive" data-testid={`folder-delete-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      )}
    </div>
  );
}

export function FolderNav({ onNavigate }) {
  const { folders, notes, folderId, setFolderId, setNoteId, refresh, view, setView, setDialog } = useVault();
  const [name, setName] = useState("");
  const [usage, setUsage] = useState(null);
  useEffect(() => { navigator.storage?.estimate?.().then(setUsage); }, [notes]);
  const add = async () => {
    if (!name.trim()) return;
    const f = { id: uuid(), name: name.trim(), icon: "folder", builtin: false, order: 100, createdAt: new Date().toISOString() };
    await db.put("folders", f); setName(""); await refresh(); setFolderId(f.id); setNoteId(null);
  };
  const select = (id) => { setFolderId(id); setNoteId(null); setView("folder"); onNavigate?.(); };
  const openCalendar = () => { setNoteId(null); setView("calendar"); onNavigate?.(); };
  const builtin = folders.filter((f) => f.builtin), custom = folders.filter((f) => !f.builtin);
  const row = (f) => (
    <FolderRow key={f.id} f={f} count={notes.filter((n) => n.folderId === f.id).length} active={view === "folder" && f.id === folderId}
      onSelect={() => select(f.id)} onDone={(deleted) => { if (deleted && f.id === folderId) select("daily-journal"); refresh(); }} />
  );
  return (
    <nav className="flex flex-col w-full h-full overflow-y-auto p-3 gap-1" data-testid="folder-nav">
      <button type="button" onClick={openCalendar} data-testid="nav-calendar-button"
        className={`flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${view === "calendar" ? "bg-accent text-accent-foreground" : "hover:bg-muted"}`}>
        <CalendarDays className="h-4 w-4" />Calendar
      </button>
      <p className="px-3 pt-4 pb-1 text-[10px] font-mono uppercase tracking-[0.2em] text-primary">Vault Folders</p>
      {builtin.map(row)}
      <p className="px-3 pt-5 pb-1 text-[10px] font-mono uppercase tracking-[0.2em] text-primary">Custom Folders</p>
      {custom.map(row)}
      {!custom.length && <p className="px-3 text-xs text-muted-foreground">No custom folders yet.</p>}
      <div className="flex gap-1 px-1 pt-2">
        <Input placeholder="New folder name" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} className="h-9" data-testid="new-folder-input" />
        <Button size="icon" variant="outline" className="h-9 w-9 shrink-0" onClick={add} data-testid="new-folder-add-button"><FolderPlus className="h-4 w-4" /></Button>
      </div>
      <p className="px-3 pt-5 pb-1 text-[10px] font-mono uppercase tracking-[0.2em] text-primary">About</p>
      <button type="button" onClick={() => { setDialog("about"); onNavigate?.(); }} data-testid="nav-about-button"
        className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors hover:bg-muted">
        <Info className="h-4 w-4 text-muted-foreground" />About &amp; Support
      </button>
      {usage && (
        <p className="mt-auto pt-6 px-3 text-[11px] font-mono text-muted-foreground" data-testid="storage-usage">
          Storage: {fmtBytes(usage.usage)} used of {fmtBytes(usage.quota)}
        </p>
      )}
    </nav>
  );
}
